<script setup lang="ts">
import { computed } from 'vue'
import dayjs from '@/lib/dayjs'
import { arbitrary as formatNumberArbitrary } from '@/helpers/numberFormatters'

import type { WebinarCalendarEvent } from '../types'

const props = defineProps<{
  event: WebinarCalendarEvent
  rounded?: boolean
  showTime?: boolean
  inactive?: boolean
}>()

const emit = defineEmits<{
  (e: 'event-click', event: WebinarCalendarEvent): void
}>()

const priceFormatted = computed(() => formatNumberArbitrary(props.event.price))
const timeStartStr = computed(() => dayjs(props.event.start_time).format('HH:mm'))

const iconColor = computed(() => {
  const e = props.event
  if (e.me_locked) return 'default'
  if (e.me_coach) return 'default'
  if (e.me_invited || e.me_coach) return 'default'
  if (e.me_purchased) return 'green'
  if (e.me_liked) return 'red'
  return 'default'
})
</script>

<template>
  <div
    class="webinar-event-cell"
    :class="{
      'webinar-event-cell_parents-allowed': event.access_policy === 'p',
      'webinar-event-cell_rounded': rounded,
      'webinar-event-cell_inactive': inactive
    }"
    @click="emit('event-click', event)"
  >
    <div class="webinar-event-cell__content">
      <span
        class="webinar-event-cell__title"
        :title="!showTime ? event.name : undefined"
        v-text="showTime ? timeStartStr : event.name"
      />
      <span
        class="webinar-event-cell__subtitle"
        :title="showTime ? event.name : event.coach_name"
        v-text="showTime ? event.name : event.coach_name"
      />
      <span v-if="event.price > 0" class="webinar-event-cell__price">
        {{ priceFormatted }}
        <v-icon size="16">hand</v-icon>
      </span>
      <span v-else class="webinar-event-cell__price"> Бесплатно </span>
    </div>

    <div class="webinar-event-cell__icon" :class="`webinar-event-cell__icon_color_${iconColor}`">
      <v-icon v-if="event.me_locked" title="Недоступно">mdi-lock-outline</v-icon>
      <v-icon v-else-if="event.me_coach" title="Вы - автор">mdi-account-circle-outline</v-icon>
      <v-icon v-else-if="event.me_invited" title="Вас пригласили">mdi-link-variant</v-icon>
      <v-icon v-else-if="event.me_purchased" title="Вы записались">mdi-check</v-icon>
      <v-icon v-else-if="event.me_liked" title="В избранном">mdi-heart</v-icon>
    </div>
  </div>
</template>

<style lang="scss">
.webinar-event-cell {
  position: relative;
  min-height: 50px;

  color: black;

  cursor: pointer;
  user-select: none;

  --fg_color: #{$orange};
  background: $lightenOrange;

  &_parents-allowed {
    --fg_color: #{$blue};
    background: $lightenBlue;
  }

  &_rounded {
    border-radius: 4px;
  }

  &_inactive {
    opacity: 0.5;
  }

  &_has-more {
    --fg_color: #{$green};
    background: $lightenGreen;
  }

  border-left: 2px var(--fg_color) solid;
  transition: opacity 0.1s ease;

  &__content {
    position: absolute;

    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    padding: 2px 32px 2px 6px;

    display: flex;
    flex-direction: column;

    // Gap between elements
    & > * + * {
      margin-top: 2px;
    }
  }

  &__icon {
    position: absolute;
    right: 4px;
    top: 4px;

    &_color {
      &_default {
        color: var(--fg_color);
      }
      &_green {
        color: $green;
      }
      &_red {
        color: $red;
      }
    }
  }

  &__title {
    font-size: 12px;
    font-weight: 600;
    line-height: 15px;

    // 16px at 50px content height
    max-height: 32%;
    min-height: 16px;

    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__subtitle {
    font-size: 10px;
    font-weight: 400;
    line-height: 12px;

    // 12px at 50px content height
    max-height: 26%;
    min-height: 12px;

    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__price {
    display: flex;
    align-items: center;

    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
  }
}
</style>
